import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiBook, FiCheckCircle, FiUsers } from "react-icons/fi";
import { settingsAPI } from "../services/api";
import { DEFAULT_PROGRAMS } from "../utils/defaultPrograms";

const colorMap = {
  blue: {
    grad: "from-blue-500 to-blue-700",
    bg: "bg-blue-50",
    border: "border-blue-100",
    text: "text-blue-600",
    dot: "bg-blue-600",
  },
  primary: {
    grad: "from-primary-500 to-primary-700",
    bg: "bg-primary-50",
    border: "border-primary-100",
    text: "text-primary-600",
    dot: "bg-primary-600",
  },
  secondary: {
    grad: "from-secondary-500 to-secondary-700",
    bg: "bg-secondary-50",
    border: "border-secondary-100",
    text: "text-secondary-700",
    dot: "bg-secondary-700",
  },
};

const Academics = () => {
  const [programs, setPrograms] = useState(DEFAULT_PROGRAMS);
  const [classSections, setClassSections] = useState([]);

  useEffect(() => {
    settingsAPI
      .get()
      .then((res) => {
        const s = res.data?.data?.settings;
        if (s?.programs?.length > 0) setPrograms(s.programs);
        if (s?.classSections?.length > 0) setClassSections(s.classSections);
      })
      .catch(() => {});
  }, []);

  const totalEnrolled = classSections.reduce(
    (sum, cs) => sum + (Number(cs.totalStudents) || 0),
    0,
  );

  return (
    <div>
      {/* Hero */}
      <div className="hero-gradient py-24 text-center relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle, #bfdbfe 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          <p className="text-secondary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            Education
          </p>
          <h1 className="text-5xl font-serif font-bold text-white mb-4">
            Academics
          </h1>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Comprehensive academic programs for every stage of your child's
            educational journey
          </p>
        </motion.div>
      </div>

      {/* Programs */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {programs.map((program, idx) => {
            const c = colorMap[program.colorScheme] || colorMap.primary;
            return (
              <motion.div
                key={program._id || idx}
                id={program._id || idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className={`${c.bg} border ${c.border} rounded-3xl overflow-hidden`}
              >
                <div className={`h-2 bg-gradient-to-r ${c.grad}`} />
                <div className="p-8 lg:p-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        {program.grades && (
                          <span
                            className={`px-3 py-1 ${c.bg} ${c.text} border ${c.border} rounded-full text-xs font-semibold`}
                          >
                            {program.grades}
                          </span>
                        )}
                        {program.age && (
                          <span className="text-gray-500 text-xs">
                            {program.age}
                          </span>
                        )}
                      </div>
                      <h2 className="text-3xl font-serif font-bold text-primary-500 mb-4">
                        {program.title}
                      </h2>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        {program.description}
                      </p>
                      {program.highlights?.length > 0 && (
                        <div className="space-y-2">
                          {program.highlights.map((h, hIdx) => (
                            <div key={hIdx} className="flex items-center gap-3">
                              <FiCheckCircle
                                className={`w-4 h-4 ${c.text} flex-shrink-0`}
                              />
                              <span className="text-gray-700 text-sm">{h}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {program.subjects?.length > 0 && (
                      <div>
                        <h3 className="font-bold text-primary-500 mb-4 flex items-center gap-2">
                          <FiBook className="w-4 h-4" /> Subjects Offered
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {program.subjects.map((subject, sIdx) => (
                            <div
                              key={sIdx}
                              className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2 text-sm text-gray-700"
                            >
                              <div
                                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`}
                              />
                              {subject}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Class-wise Enrollment — dynamic */}
      {classSections.length > 0 && (
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="section-subtitle mb-2">Our Numbers</p>
              <h2 className="section-title mb-4">Class-wise Enrollment</h2>
              <p className="text-gray-500">
                {totalEnrolled} students across {classSections.length} class
                sections
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm"
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-primary-500 text-white">
                      <th className="text-left px-5 py-3 font-semibold">Class</th>
                      <th className="text-left px-5 py-3 font-semibold">Section</th>
                      <th className="text-right px-5 py-3 font-semibold">
                        Total Students
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {classSections.map((cs, idx) => (
                      <tr
                        key={cs._id || idx}
                        className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-5 py-3 font-medium text-gray-800">
                          {cs.class}
                        </td>
                        <td className="px-5 py-3 text-gray-600">
                          Section {cs.section}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-flex items-center gap-1.5 font-bold text-primary-600">
                            <FiUsers className="w-3.5 h-3.5" />
                            {cs.totalStudents}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Academics;
